import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutGrid, Kanban, FileText, FileSearch, Building2, Package, BarChart3, Sparkles, Settings, LogOut, ArrowUpRight, Sunrise, Radar, Globe2, Target, Inbox } from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/common";
import { MediabilityLogo } from "@/components/Logo";

const NAV = [
  {
    items: [
      { to: "/overview", label: "Overview", icon: LayoutGrid, testId: "sidebar-nav-overview-link" },
      { to: "/inbox", label: "Inbox", icon: Inbox, testId: "sidebar-nav-inbox-link", badge: "inbox" },
      { to: "/workspace", label: "AI Workspace", icon: Sparkles, testId: "sidebar-nav-workspace-link" },
    ],
  },
  {
    label: "Sales Intelligence",
    items: [
      { to: "/intel/today", label: "Today", icon: Sunrise, testId: "sidebar-nav-intel-today-link" },
      { to: "/intel/radar", label: "Project Radar", icon: Radar, testId: "sidebar-nav-intel-radar-link" },
      { to: "/intel/companies", label: "Companies", icon: Globe2, testId: "sidebar-nav-intel-companies-link" },
      { to: "/intel/opportunities", label: "Intel pipeline", icon: Target, testId: "sidebar-nav-intel-opportunities-link" },
    ],
  },
  {
    label: "Sales",
    items: [
      { to: "/pipeline", label: "Opportunities", icon: Kanban, testId: "sidebar-nav-opportunities-link" },
      { to: "/quotes", label: "Quotes", icon: FileText, testId: "sidebar-nav-quotes-link" },
      { to: "/tenders", label: "Tenders", icon: FileSearch, testId: "sidebar-nav-tenders-link" },
    ],
  },
  {
    label: "Workspace",
    items: [
      { to: "/customers", label: "Customers", icon: Building2, testId: "sidebar-nav-customers-link" },
      { to: "/products", label: "Products", icon: Package, testId: "sidebar-nav-products-link" },
      { to: "/insights", label: "Insights", icon: BarChart3, testId: "sidebar-nav-insights-link" },
    ],
  },
];

/** Unread count for the Inbox badge - refreshed on mount, when the inbox changes (window event) and every minute. */
function useInboxUnread(enabled) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const load = () => api.get("/inbox/summary").then((r) => alive && setN(r.data?.unread || 0)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("inbox:changed", load);
    return () => {
      alive = false;
      clearInterval(t);
      window.removeEventListener("inbox:changed", load);
    };
  }, [enabled]);
  return n;
}

function Item({ to, label, icon: Icon, testId, end, onNavigate, forceActive, count }) {
  const isOn = (isActive) => (forceActive === undefined ? isActive : forceActive);
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "group relative flex h-[34px] items-center gap-2.5 rounded-lg px-2.5 text-[13.5px] font-medium text-foreground/70 hover:bg-black/[0.045] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40",
          isOn(isActive) && "bg-white text-foreground shadow-[0_1px_2px_hsl(30_12%_18%/0.06),0_0_0_1px_hsl(30_12%_18%/0.05)] hover:bg-white"
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={cn("h-4 w-4 text-muted-foreground transition-colors group-hover:text-foreground", isOn(isActive) && "text-primary")} strokeWidth={isOn(isActive) ? 2.2 : 1.9} />
          <span className="truncate">{label}</span>
          {count ? (
            <span className="ml-auto inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1.5 font-mono text-[10.5px] font-semibold tabular-nums text-white" data-testid={`${testId}-badge`}>
              {count > 99 ? "99+" : count}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );
}

/**
 * Main navigation. `activePath` (optional) overrides router-based active state - used by static previews
 * that render the sidebar outside the app shell. `className` merges into the <aside>.
 */
export function Sidebar({ onOpenAI, onNavigate, activePath, className }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const force = (to) => (activePath === undefined ? undefined : to === activePath);
  // static previews (activePath set) never call the API
  const inboxUnread = useInboxUnread(activePath === undefined && Boolean(user));
  const badges = { inbox: activePath === undefined ? inboxUnread : 7 };
  return (
    <aside className={cn("flex h-screen w-[248px] shrink-0 flex-col border-r border-black/[0.06] bg-[hsl(var(--sidebar))]", className)} data-testid="sidebar">
      <div className="px-[22px] pb-4 pt-5">
        <NavLink to="/overview" onClick={onNavigate} className="inline-flex rounded-md focus-visible:ring-2 focus-visible:ring-ring/40" data-testid="sidebar-logo-link" aria-label="Mediability - Overview">
          <MediabilityLogo className="h-[26px]" testId="sidebar-logo" />
        </NavLink>
      </div>

      <nav className="flex-1 space-y-5 overflow-y-auto px-3 scrollbar-thin" aria-label="Main">
        {NAV.map((group, gi) => (
          <div key={group.label || gi}>
            {group.label ? <div className="eyebrow mb-1.5 px-2.5 text-[10px]">{group.label}</div> : null}
            <div className="space-y-0.5">
              {group.items.map((c) => (
                <Item key={c.to} {...c} onNavigate={onNavigate} forceActive={force(c.to)} count={c.badge ? badges[c.badge] : 0} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="space-y-3 px-3 pb-3 pt-2">
        <NavLink
          to="/workspace"
          onClick={onNavigate}
          data-testid="sidebar-ai-workspace-button"
          className="group relative flex w-full flex-col overflow-hidden rounded-xl bg-[hsl(var(--ink))] p-3.5 text-left text-white shadow-[0_1px_2px_hsl(0_0%_0%/0.2),0_12px_28px_-14px_hsl(0_0%_0%/0.6)] hover:bg-black focus-visible:ring-2 focus-visible:ring-ring/60"
        >
          <span className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full bg-[hsl(10_76%_61%/0.35)] blur-2xl transition-transform duration-500 group-hover:scale-125" />
          <span className="relative flex items-center justify-between">
            <span className="flex items-center gap-2 text-[13px] font-semibold">
              <Sparkles className="h-4 w-4 text-[hsl(10_80%_70%)]" /> AI Workspace
            </span>
          </span>
          <span className="relative mt-1.5 text-[12px] leading-snug text-white/60">From inquiry to signed proposal - designed, priced and written with you.</span>
          <span className="relative mt-2 inline-flex items-center gap-1 text-[11.5px] font-medium text-white/80 transition-colors group-hover:text-white">Start a workspace <ArrowUpRight className="h-3 w-3" /></span>
        </NavLink>

        <div className="space-y-0.5">
          <Item to="/settings" label="Settings" icon={Settings} testId="sidebar-nav-settings-link" onNavigate={onNavigate} forceActive={force("/settings")} />
        </div>

        <div className="flex items-center gap-2.5 rounded-xl border border-black/[0.06] bg-white px-2.5 py-2">
          <UserAvatar name={user?.name} initials={user?.initials} color={user?.color} src={user?.avatar_url} size="lg" className="h-9 w-9" testId="sidebar-user-avatar" />
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate text-[13px] font-semibold" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="truncate text-[11px] text-muted-foreground">{user?.title || user?.role}</div>
          </div>
          <button
            type="button"
            title="Sign out"
            aria-label="Sign out"
            data-testid="sidebar-logout-button"
            onClick={() => {
              logout();
              navigate("/login");
            }}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-black/[0.05] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
